import { useEffect, useCallback, useState } from 'react';

/**
 * Keyboard shortcuts configuration
 */
export const SHORTCUTS = {
  GENERATE: { key: 'g', ctrl: true, description: 'Generate content' },
  SCHEDULE: { key: 's', ctrl: true, description: 'Open schedule modal' },
  ESCAPE: { key: 'Escape', ctrl: false, description: 'Close modal' },
  HELP: { key: '/', ctrl: true, description: 'Show keyboard shortcuts' },
  SAVE: { key: 's', ctrl: true, shift: true, description: 'Save as template' },
  COPY: { key: 'c', ctrl: true, shift: true, description: 'Copy content' },
  NEW: { key: 'n', ctrl: true, description: 'New content' },
};

/**
 * Format shortcut for display
 */
export const formatShortcut = (shortcut) => {
  const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;
  const parts = [];
  
  if (shortcut.ctrl) parts.push(isMac ? '⌘' : 'Ctrl');
  if (shortcut.shift) parts.push(isMac ? '⇧' : 'Shift');
  if (shortcut.alt) parts.push(isMac ? '⌥' : 'Alt');
  
  const key = shortcut.key === 'Escape' ? 'Esc' : shortcut.key.toUpperCase();
  parts.push(key);
  
  return parts.join(isMac ? '' : '+');
};

/**
 * Hook to register keyboard shortcuts
 * @param {Object} handlers - Object mapping shortcut names to handler functions
 * @param {boolean} enabled - Whether shortcuts are enabled (disable in input fields)
 */
export const useKeyboardShortcuts = (handlers = {}, enabled = true) => {
  const [showHelp, setShowHelp] = useState(false);

  const handleKeyDown = useCallback((event) => {
    if (!enabled) return;
    
    // Don't trigger shortcuts when typing in inputs
    const target = event.target;
    const isInputField = target.tagName === 'INPUT' || 
                         target.tagName === 'TEXTAREA' || 
                         target.isContentEditable;
    
    // Allow Escape in input fields
    if (isInputField && event.key !== 'Escape') return;

    const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;
    const ctrlOrCmd = isMac ? event.metaKey : event.ctrlKey;

    // Check each shortcut
    Object.entries(SHORTCUTS).forEach(([name, shortcut]) => {
      const keyMatch = event.key.toLowerCase() === shortcut.key.toLowerCase();
      const ctrlMatch = shortcut.ctrl ? ctrlOrCmd : !ctrlOrCmd;
      const shiftMatch = shortcut.shift ? event.shiftKey : !event.shiftKey;
      
      if (keyMatch && ctrlMatch && shiftMatch) {
        // Special case for help
        if (name === 'HELP') {
          event.preventDefault();
          setShowHelp(prev => !prev);
          return;
        }
        
        // Call the handler if registered
        if (handlers[name]) {
          event.preventDefault();
          handlers[name](event);
        }
      }
    });
  }, [handlers, enabled]);

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  return { showHelp, setShowHelp };
};

/**
 * Global keyboard shortcuts provider component
 */
export const KeyboardShortcutsProvider = ({ children, handlers }) => {
  useKeyboardShortcuts(handlers);
  return children;
};

export default useKeyboardShortcuts;
